import { NgModule } from "@angular/core";
import { RouterModule, Routes, UrlMatchResult, UrlSegment } from "@angular/router";
import { UnsavedChangesGuard } from "src/app/shared/services/guard-services/unsaved-changes-guard.service";
import { FileDetailComponent } from "./components";
import { DigitalMemoryLogListComponent } from "./components/digital-memory-log-list/digital-memory-log-list.component";
import { PreviewComponent } from "./components/preview/preview.component";
import { TravelKitModifyComponent } from "./components/travel-kit-modify/travel-kit-modify";

// Custom matcher for create-digital-memory-log-{id} format
export function createDigitalMemoryLogMatcher(url: UrlSegment[]): UrlMatchResult {
  console.log("createDigitalMemoryLogMatcher called with url:", url);

  if (url.length === 1 && url[0].path.startsWith("create-digital-memory-log-")) {
    const groupId = url[0].path.slice("create-digital-memory-log-".length);

    if (groupId && /^\d+$/.test(groupId)) {
      return {
        consumed: url,
        posParams: {
          groupId: new UrlSegment(groupId, {}),
          mode: new UrlSegment("create", {}),
        },
      };
    }
  }
  return null;
}

// Custom matcher for edit-digital-memory-log-{id} format
export function editDigitalMemoryLogMatcher(url: UrlSegment[]): UrlMatchResult {
  if (url.length === 1 && url[0].path.startsWith("edit-digital-memory-log-")) {
    const groupId = url[0].path.slice("edit-digital-memory-log-".length);
    console.log("Extracted groupId:", groupId);

    if (groupId && /^\d+$/.test(groupId)) {
      // Validate that it's a number
      return {
        consumed: url,
        posParams: {
          groupId: new UrlSegment(groupId, {}),
          mode: new UrlSegment("edit", {}),
        },
      };
    }
  }
  return null;
}

// Custom matcher for preview-{name}-{id} format
export function previewDigitalMemoryLogMatcher(url: UrlSegment[]): UrlMatchResult {
  if (url.length === 1 && url[0].path.startsWith("preview-") && url[0].path.includes("-")) {
    const fullPath = url[0].path;
    const parts = fullPath.split("-");

    // Should have at least 3 parts: 'preview', name parts, and id
    if (parts.length >= 3 && parts[0] === "preview") {
      const lastPart = parts[parts.length - 1];

      // Check if last part is a number (previewId)
      if (/^\d+$/.test(lastPart)) {
        const previewId = lastPart;
        const previewName = parts.slice(1, -1).join("-"); // Everything between 'preview-' and '-{id}'

        return {
          consumed: url,
          posParams: {
            previewId: new UrlSegment(previewId, {}),
            previewName: new UrlSegment(previewName, {}),
          },
        };
      }
    }
  }

  return null;
}

// Custom matcher for preview-{name}-{id}/file-detail-{name}-{id} format
export function previewFileDetailMatcher(url: UrlSegment[]): UrlMatchResult {
  if (url.length === 2 && url[0].path.startsWith("preview-") && url[1].path.startsWith("file-detail-")) {
    // Parse preview segment
    const previewPath = url[0].path;
    const previewParts = previewPath.split("-");

    // Parse file-detail segment
    const fileDetailPath = url[1].path;
    const fileDetailParts = fileDetailPath.split("-");

    // Validate preview format: preview-{name}-{id}
    if (previewParts.length >= 3 && previewParts[0] === "preview") {
      const previewLastPart = previewParts[previewParts.length - 1];

      // Validate file-detail format: file-detail-{name}-{id}
      if (fileDetailParts.length >= 3 && fileDetailParts[0] === "file" && fileDetailParts[1] === "detail") {
        const fileDetailLastPart = fileDetailParts[fileDetailParts.length - 1];

        // Both last parts should be numbers
        if (/^\d+$/.test(previewLastPart) && /^\d+$/.test(fileDetailLastPart)) {
          const previewId = previewLastPart;
          const previewName = previewParts.slice(1, -1).join("-");
          const subSectionId = fileDetailLastPart;
          const subSectionName = fileDetailParts.slice(2, -1).join("-");

          return {
            consumed: url,
            posParams: {
              previewId: new UrlSegment(previewId, {}),
              previewName: new UrlSegment(previewName, {}),
              subSectionId: new UrlSegment(subSectionId, {}),
              subSectionName: new UrlSegment(subSectionName, {}),
            },
          };
        }
      }
    }
  }

  console.log("Preview file detail matcher failed");
  return null;
}

const routes: Routes = [
  {
    path: "",
    // component: DigitalLayoutComponent,
    children: [
      {
        path: "",
        component: DigitalMemoryLogListComponent,
      },
      {
        matcher: createDigitalMemoryLogMatcher,
        component: TravelKitModifyComponent,
        canDeactivate: [UnsavedChangesGuard],
      },
      {
        matcher: editDigitalMemoryLogMatcher,
        component: TravelKitModifyComponent,
        canDeactivate: [UnsavedChangesGuard],
      },
      // Custom matcher for preview-{name}-{id} format
      {
        matcher: previewDigitalMemoryLogMatcher,
        component: PreviewComponent,
      },
      // Custom matcher for preview-{name}-{id}/file-detail-{name}-{id} format
      {
        matcher: previewFileDetailMatcher,
        component: FileDetailComponent,
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class DigitalMemoryLogRoutingModule {}
